<script>
  import { mapActions, mapState } from 'vuex'
  import { throttle } from 'lodash-es'
  import uuidMixin from '@/assets/js/mixin/uuid'
  import { overlayInstanceIds } from '@/assets/js/model/overlay-instance'

  export default {
    mixins: [uuidMixin],
    props: {
      label: {
        type: String,
        default: null,
      },
      position: {
        type: Function,
        required: true,
      },
      transition: {
        type: String,
        required: true,
      },
      originPosition: {
        type: Object,
        required: true,
      },
      overlayPosition: {
        type: Object,
        required: true,
      },
      instanceId: {
        type: String,
        default() {
          return overlayInstanceIds.BASE_TOOLTIP
        },
      },
      isEnabled: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        isOpen: false,
        isUnmounting: false,
      }
    },
    computed: {
      ...mapState('overlay', ['overlays']),
      ...mapState('scroll', { scrollPosition: 'position' }),
      ...mapState('viewport', { viewportWidth: 'width', viewportHeight: 'height' }),
      overlayUuid() {
        return this.isOpen ? this.overlays[this.instanceId].uuid : null
      },
      positionAttrs() {
        return [
          this.originPosition,
          this.overlayPosition,
          this.transition,
          this.position,
          this.scrollPosition,
          this.viewportWidth,
          this.viewportHeight,
        ].join('-')
      },
      slotApi() {
        return {
          toggle: this.toggle,
          hasHover: process.client ? window.matchMedia('(hover: none)').matches : false,
          isTouch: process.client ? window.matchMedia('(any-pointer: coarse)').matches : false,
        }
      },
    },
    watch: {
      isEnabled() {
        if (!this.isEnabled) {
          this.close()
        }
      },
      positionAttrs() {
        if (this.isOpen) {
          this.updateOverlay({
            id: this.instanceId,
            props: {
              position: this.position(this.$el.getBoundingClientRect()),
              label: this.label,
              originPosition: this.originPosition,
              overlayPosition: this.overlayPosition,
            },
          })
        }
      },
    },
    mounted() {
      document.addEventListener('scroll', this.throttledOnScroll, true)
    },
    beforeDestroy() {
      document.removeEventListener('scroll', this.throttledOnScroll, true)

      this.isUnmounting = true

      if (this.isOpen) {
        this.close()
      }
    },
    methods: {
      ...mapActions('overlay', ['closeOverlay', 'openOverlay', 'updateOverlay']),
      throttledOnScroll: throttle(function () {
        this.$emit('scroll', this.slotApi)
      }, 100),
      toggle(value) {
        switch (true) {
          case value == null:
            if (this.isOpen) {
              this.close()
            } else {
              this.open()
            }
            break
          case value === false:
            this.close()
            break
          case value === true:
            this.open()
            break
        }
      },
      open() {
        if (!this.isEnabled || this.isUnmounting) {
          return
        }

        this.isOpen = true

        this.openOverlay({
          id: this.instanceId,
          transition: this.transition,
          disableScroll: false,
          facets: ['tooltip'],
          component: 'overlay-tooltip-base-content',
          props: {
            position: this.position(this.$el.getBoundingClientRect()),
            label: this.label,
            originPosition: this.originPosition,
            overlayPosition: this.overlayPosition,
          },
        })
      },
      close() {
        this.isOpen = false

        this.closeOverlay({
          id: this.instanceId,
        })
      },
    },
    render() {
      return this.$scopedSlots.default(this.slotApi)[0]
    },
  }
</script>
